// ai.js — Claude client for the AI coach, quick-log parsing, debriefs and
// weekly reviews.
//
// Two ways to reach Claude, picked automatically (aiMode()):
//   'byo'    the user's own Anthropic API key (Settings → AI Coach), called
//            directly from the browser. Unlimited, every model incl. Opus 5.5.
//            The key lives in its own localStorage entry (never in app state,
//            exports or backups).
//   'relay'  the free hosted relay at the app's own origin (<api base>/ai,
//            server/ai_relay.js): the server's key, Haiku 4.5 + Sonnet 5.5, a
//            daily per-device budget. Used when the user has no key and the
//            server reports it enabled (GET <api base>/ai/status).
//   'off'    neither — AI features show how to turn them on.
//
// Model routing: quick-log parsing, debriefs and weekly reviews → Claude Haiku
// 4.5 (fast, cheap); the coach → Claude Sonnet 5.5 by default, Claude Opus 5.5
// selectable with your own key.
//
// The SDK is imported lazily so users who never use AI don't pay for it in the
// initial bundle.

import React from 'react';
import { COACH_TOOLS, WRITE_TOOLS, CARD_READ_TOOLS, runReadTool, coachContext, contextBlockFor, weeklyReviewPrompt, WEEKLY_REVIEW_SCHEMA, normalizeReview, buildWorkoutPlan, planForModel, validateProposal } from './coach_tools.js';

const AI_KEY = 'liftlog.ai';
const DEVICE_KEY = 'liftlog.ai.device';
const RELAY_KEY = 'liftlog.ai.relay';
const USAGE_KEY = 'liftlog.ai.usage';
const CFG_VERSION = 2;

export const DEFAULT_COACH_MODEL = 'claude-sonnet-5-5';
// Quick log, debriefs and weekly reviews: Sonnet 5.5 at low effort.
export const FAST_MODEL = 'claude-sonnet-5-5';
export const FAST_EFFORT = 'low';
// Adaptive thinking counts against max_tokens; one-shot tasks get this much extra room.
const THINK_HEADROOM = 2000;
export const AI_MODELS = [
  { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5', note: 'Sharp, fast coaching · default', free: true },
  { id: 'claude-opus-5-5', label: 'Claude Opus 5.5', note: 'Deepest reasoning · needs your own key', free: false },
  { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5', note: 'Quickest replies · lightest', free: true },
];
export const modelLabel = (id) => AI_MODELS.find(m => m.id === id)?.label || id;

// List prices, $ per million tokens (cache writes are 5-minute, 1.25× input).
export const PRICES = {
  'claude-opus-5-5': { in: 4, out: 20, cacheRead: 0.20, cacheWrite: 5 },
  'claude-sonnet-5-5': { in: 2, out: 10, cacheRead: 0.20, cacheWrite: 2.5 },
  'claude-haiku-4-5': { in: 1, out: 5, cacheRead: 0.10, cacheWrite: 1.25 },
};

// ——— storage helpers (safe in node / private mode) ———————————————————————
const ls = () => { try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch { return null; } };
const lsGet = (k, d) => { try { const v = ls()?.getItem(k); return v == null ? d : JSON.parse(v); } catch { return d; } };
const lsSet = (k, v) => { try { ls()?.setItem(k, JSON.stringify(v)); } catch { /* full / blocked */ } };

// ——— config ——————————————————————————————————————————————————————————————
export function getAIConfig() {
  const c = lsGet(AI_KEY, {}) || {};
  // v2: the coach defaults to Sonnet 5.5 (v1 stored Opus as everyone's default).
  const model = c.v === CFG_VERSION && AI_MODELS.some(m => m.id === c.model) ? c.model : DEFAULT_COACH_MODEL;
  return { apiKey: c.apiKey || '', model };
}
export function setAIConfig(patch) {
  const next = { ...getAIConfig(), ...patch, v: CFG_VERSION };
  lsSet(AI_KEY, next);
  _client = null;
  emit();
  return next;
}

// ——— status store (React-subscribable) ——————————————————————————————————
const listeners = new Set();
let version = 0;
function emit() { version++; for (const fn of listeners) { try { fn(); } catch { /* ignore */ } } }
export function subscribeAI(fn) { listeners.add(fn); return () => listeners.delete(fn); }
/** Re-renders on mode / quota / config changes. → { mode, relay, config, usage } */
export function useAIStatus() {
  React.useSyncExternalStore(subscribeAI, () => version, () => version);
  React.useEffect(() => { refreshRelayStatus(); }, []);
  return { mode: aiMode(), relay: relayInfo(), config: getAIConfig() };
}

// ——— relay ——————————————————————————————————————————————————————————————
/** API base: localStorage 'liftlog.syncBase' (dev) → VITE_SYNC_BASE → same-origin '/api'. */
export function apiBase() {
  try { const b = ls()?.getItem('liftlog.syncBase'); if (b) return b.replace(/\/$/, ''); } catch { /* ignore */ }
  let v = '';
  try { v = import.meta.env && import.meta.env.VITE_SYNC_BASE; } catch { /* not vite */ }
  return (v || '/api').replace(/\/$/, '');
}
function absolute(u) {
  try { return new URL(u, typeof location !== 'undefined' ? location.href : 'http://localhost/').href.replace(/\/$/, ''); } catch { return u; }
}
export function deviceId() {
  let id = lsGet(DEVICE_KEY, null);
  if (typeof id !== 'string' || !/^[0-9a-f]{32}$/.test(id)) {
    const b = new Uint8Array(16);
    (globalThis.crypto || {}).getRandomValues?.(b);
    id = [...b].map(x => x.toString(16).padStart(2, '0')).join('');
    lsSet(DEVICE_KEY, id);
  }
  return id;
}
/** Last known relay status: { enabled, quota: { limit, used, remaining, resetAt } | null, checkedAt, error? } */
export function relayInfo() {
  const r = lsGet(RELAY_KEY, null);
  return r && typeof r === 'object' ? r : { enabled: false, quota: null, checkedAt: 0 };
}
let _refreshing = null;
/** Ask the server whether the free relay is on (and how much of today's budget is left). */
export function refreshRelayStatus({ force = false } = {}) {
  const prev = relayInfo();
  if (!force && _refreshing) return _refreshing;
  if (!force && prev.checkedAt && Date.now() - prev.checkedAt < 60000) return Promise.resolve(prev);
  if (typeof fetch === 'undefined') return Promise.resolve(prev);
  _refreshing = (async () => {
    let next = prev;
    try {
      const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const t = setTimeout(() => ac?.abort(), 6000);
      const r = await fetch(`${apiBase()}/ai/status`, { headers: { 'x-liftlog-device': deviceId() }, signal: ac?.signal, cache: 'no-store' });
      clearTimeout(t);
      if (r.ok) {
        const j = await r.json();
        next = { enabled: !!j.enabled, quota: j.quota || null, models: j.models || null, checkedAt: Date.now() };
      } else if (r.status === 404) {
        next = { enabled: false, quota: null, checkedAt: Date.now() }; // a server without the relay
      } else {
        next = { ...prev, checkedAt: Date.now(), error: 'server' };
      }
    } catch {
      // Offline / unreachable: keep the last known answer (the AI features then
      // explain they need a connection) but note the failure.
      next = { ...prev, checkedAt: Date.now(), error: 'unreachable' };
    }
    lsSet(RELAY_KEY, next);
    _refreshing = null;
    emit();
    return next;
  })();
  return _refreshing;
}
function noteQuota(patch) {
  const r = relayInfo();
  lsSet(RELAY_KEY, { ...r, quota: { ...(r.quota || {}), ...patch } });
  emit();
}

export function aiMode() {
  if (getAIConfig().apiKey) return 'byo';
  if (relayInfo().enabled) return 'relay';
  return 'off';
}
export const aiEnabled = () => aiMode() !== 'off';

// Kick off the status check as soon as the app loads (ai.js is in the main bundle).
if (typeof window !== 'undefined') setTimeout(() => { refreshRelayStatus(); }, 50);

// ——— model routing ————————————————————————————————————————————————————————
/** task: 'coach' | 'quicklog' | 'debrief' | 'review' | anything else (→ fast model). */
export function modelFor(task, { mode = aiMode(), coachModel = getAIConfig().model } = {}) {
  if (task !== 'coach') return FAST_MODEL;
  const m = AI_MODELS.some(x => x.id === coachModel) ? coachModel : DEFAULT_COACH_MODEL;
  if (mode === 'relay' && !AI_MODELS.find(x => x.id === m)?.free) return DEFAULT_COACH_MODEL;
  return m;
}

/** Per-model request options. Haiku 4.5 takes no effort / fallbacks. */
export function requestParams(model, mode, { effort = 'medium' } = {}) {
  if (model === 'claude-haiku-4-5') return {};
  const p = { output_config: { effort } };
  // Server-side refusal fallbacks on the user's own key (the free relay keeps costs fixed).
  if (mode === 'byo') { p.betas = ['server-side-fallback-2026-07-01']; p.fallbacks = 'default'; }
  return p;
}

/**
 * Prompt caching: the system prompt and the training snapshot carry their own
 * breakpoints; this puts the third on the last block of the last message so
 * every hop re-reads the conversation so far from cache. String contents are
 * normalized to text blocks so the cached prefix is byte-identical next time.
 */
export function withCacheBreakpoints(messages) {
  const out = messages.map(m => (typeof m.content === 'string' ? { ...m, content: [{ type: 'text', text: m.content }] } : m));
  const li = out.length - 1;
  if (li < 0) return out;
  const blocks = out[li].content.slice();
  let i = blocks.length - 1;
  while (i >= 0 && (blocks[i].type === 'thinking' || blocks[i].type === 'redacted_thinking')) i--;
  if (i >= 0) {
    blocks[i] = { ...blocks[i], cache_control: { type: 'ephemeral' } };
    out[li] = { ...out[li], content: blocks };
  }
  return out;
}

// ——— client ——————————————————————————————————————————————————————————————
let _client = null, _clientKey = null, _testFactory = null;
/** Test hook: replace the SDK client ((mode) => client-like object). */
export function __setClientFactory(fn) { _testFactory = fn; _client = null; }

async function client(mode = aiMode()) {
  if (_testFactory) return _testFactory(mode);
  if (mode === 'off') throw new AIError('no_key', 'The AI coach isn’t available yet. Add your Anthropic API key in Settings → AI Coach.');
  const { apiKey } = getAIConfig();
  const key = mode === 'byo' ? `byo:${apiKey}` : `relay:${apiBase()}`;
  if (_client && _clientKey === key) return _client;
  const { default: Anthropic } = await import('@anthropic-ai/sdk');
  // The SDK default is 10 min: an overloaded API (529) or a stalled connection
  // must not leave the coach "thinking" for minutes. Streams send headers within
  // a second or two, so 30 s to first byte with one retry on the user's key. The
  // relay may run a whole completion before its first byte (the claude-cli dev
  // upstream), so it gets longer; the relay enforces its own upstream timeout.
  _client = mode === 'byo'
    ? new Anthropic({ apiKey, dangerouslyAllowBrowser: true, maxRetries: 1, timeout: 30 * 1000 })
    : new Anthropic({
      apiKey: 'liftlog-relay', // placeholder: the relay uses the server's key and ignores this
      baseURL: absolute(`${apiBase()}/ai`),
      dangerouslyAllowBrowser: true, maxRetries: 1, timeout: 75 * 1000,
      defaultHeaders: { 'x-liftlog-device': deviceId() },
    });
  _clientKey = key;
  return _client;
}

export class AIError extends Error {
  constructor(code, message, extra = {}) { super(message); this.code = code; Object.assign(this, extra); }
}

export function fmtResetTime(ms) {
  if (!ms) return 'tomorrow';
  const d = new Date(ms);
  const sameDay = new Date().toDateString() === d.toDateString();
  const t = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return sameDay ? `at ${t}` : `tomorrow at ${t}`;
}

// Map SDK / relay errors to a message a lifter can act on.
async function friendly(err, mode = aiMode()) {
  if (err instanceof AIError) return err;
  if (err?.name === 'AbortError' || err?.constructor?.name === 'APIUserAbortError') return new AIError('aborted', 'Stopped.');
  let Anthropic = null;
  try { Anthropic = (await import('@anthropic-ai/sdk')).default; } catch { /* test env */ }
  const body = err?.error && typeof err.error === 'object' ? err.error : null;
  const lift = body?.liftlog || null;
  if (mode === 'relay' && lift) {
    if (lift.reason === 'quota') {
      noteQuota({ remaining: 0, resetAt: lift.resetAt || null });
      return new AIError('quota', lift.scope === 'global'
        ? `The free AI coach has hit today's limit. It resets ${fmtResetTime(lift.resetAt)} — or add your own API key for unlimited use.`
        : `You've used today's free AI coaching. It resets ${fmtResetTime(lift.resetAt)} — or add your own API key for unlimited use.`, { resetAt: lift.resetAt });
    }
    if (lift.reason === 'size') return new AIError('too_long', 'This conversation is too long for the free coach. Tap New to start a fresh one.');
    if (lift.reason === 'disabled') { refreshRelayStatus({ force: true }); return new AIError('relay_off', 'The free AI coach is turned off on this server. Add your own API key in Settings → AI Coach.'); }
    if (lift.reason === 'concurrent') return new AIError('busy', 'The coach is still answering your last message — try again in a moment.');
    if (lift.reason === 'model') return new AIError('perm', 'That model needs your own API key. Pick Sonnet 5.5 or Haiku 4.5, or add a key in Settings → AI Coach.');
  }
  const status = err?.status;
  const is = (cls) => Anthropic && Anthropic[cls] && err instanceof Anthropic[cls];
  if (is('AuthenticationError') || (status === 401 && mode === 'byo')) return new AIError('auth', 'That API key was rejected. Check it in Settings → AI Coach.');
  if (is('PermissionDeniedError') || status === 403) return new AIError('perm', 'This API key does not have access to the selected model.');
  if (is('RateLimitError') || status === 429) return new AIError('rate', 'Rate limited — wait a moment and try again.');
  if (is('BadRequestError') || status === 400) return new AIError('bad', `The request was rejected: ${body?.error?.message || err.message}`);
  if (is('APIConnectionTimeoutError')) return new AIError('timeout', 'The AI service took too long to answer. Try again in a moment.');
  if (status === 529 || body?.error?.type === 'overloaded_error') return new AIError('overloaded', 'The AI service is overloaded right now. Try again in a minute.');
  if (is('APIConnectionError') || (err && !status && /fetch|network|connect/i.test(err.message || ''))) {
    return new AIError('offline', mode === 'relay'
      ? "Can't reach the Lift Log server — the coach needs a connection (the rest of the app works offline)."
      : "Can't reach the AI service — the coach needs a connection (the rest of the app works offline).");
  }
  if (status === 413) return new AIError('too_long', 'This conversation is too long. Tap New to start a fresh one.');
  // An error event inside a stream (no HTTP status): map by its type, never show raw JSON.
  const et = body?.error?.type || body?.type;
  if (!status && et) {
    if (et === 'overloaded_error') return new AIError('overloaded', 'The AI service is overloaded right now. Try again in a minute.');
    if (et === 'rate_limit_error') return new AIError('rate', 'Rate limited — wait a moment and try again.');
    return new AIError('api', /too long/i.test(body?.error?.message || '') ? 'The AI took too long to answer. Try again in a moment.' : 'The AI service had a problem. Try again shortly.');
  }
  if (status) return new AIError('api', `AI service error (${status}). Try again shortly.`);
  return new AIError('unknown', err?.message || 'Something went wrong.');
}

// ——— usage readout (own key) ————————————————————————————————————————————
export function getUsage() {
  const u = lsGet(USAGE_KEY, null);
  return u && u.byModel ? u : { since: null, byModel: {} };
}
export function resetUsage() { lsSet(USAGE_KEY, { since: Date.now(), byModel: {} }); emit(); }
export function recordUsage(model, usage, mode = aiMode()) {
  if (mode !== 'byo' || !usage) return;
  const u = getUsage();
  const m = u.byModel[model] || { in: 0, out: 0, cacheRead: 0, cacheWrite: 0, requests: 0 };
  m.in += usage.input_tokens || 0;
  m.out += usage.output_tokens || 0;
  m.cacheRead += usage.cache_read_input_tokens || 0;
  m.cacheWrite += usage.cache_creation_input_tokens || 0;
  m.requests += 1;
  lsSet(USAGE_KEY, { since: u.since || Date.now(), byModel: { ...u.byModel, [model]: m } });
  emit();
}
const usageRow = (u) => ({ in: u.input_tokens || 0, out: u.output_tokens || 0, cacheRead: u.cache_read_input_tokens || 0, cacheWrite: u.cache_creation_input_tokens || 0 });
/** Estimated $ for a usage row on `model` at list prices. */
export function costOf(model, m) {
  const p = PRICES[model];
  if (!p || !m) return 0;
  return (m.in * p.in + m.out * p.out + m.cacheRead * p.cacheRead + m.cacheWrite * p.cacheWrite) / 1e6;
}

// ——— the coach ————————————————————————————————————————————————————————————

const COACH_SYSTEM = `You are the AI strength coach inside Lift Log, a workout-tracking app. You coach one lifter using their real training data.

How to work:
- Ground every claim in the user's data. The snapshot below is computed on-device from their log; call tools for details (get_exercise_history, get_recent_workouts, get_program, get_weekly_volume). Never invent numbers, sessions, sleep, or other data you were not given. If data is missing, say so.
- Be evidence-based: progressive overload, 10–20 hard sets per muscle per week for most lifters, most sets 0–3 reps from failure, deloads when performance and effort trends say so. Explain the "why" in one line.
- Be concise and mobile-friendly: short paragraphs or tight bullet lists, bold the key numbers, no headings, no tables. Default under 150 words unless asked for more.
- To change the program, call propose_program_changes (one batched call). To create a program, first search_exercises, then call propose_new_program — pick a progression scheme per exercise that fits the request (e.g. 'percent' on a 5/3/1 wave for a strength program, 'rp_sets' for a hypertrophy block, 'linear' for a novice) and give the program a matching cycle. These render as cards the user approves — don't ask for permission in prose first, and don't claim a change was applied.
- Technique and form questions: call get_exercise_guide and base your cues on its steps (plus the lifter's own history if relevant). Give 3–5 cues, the most common mistake, and one regression or substitution.
- When the user wants a session for today (or isn't following their program today), call build_workout; it renders a card with a Start button. Mention the plan's key points instead of listing every set.
- A message that starts with "[context]" tells you where in the app the user opened the coach (an exercise, a PR, a stalled lift, a finished workout). Look that data up with the tools before answering.
- For pain or injury: be conservative, suggest pain-free substitutions and reduced load, and recommend a medical professional for sharp, worsening, or persistent pain. Don't diagnose.
- Weights: tool data is in lb; if the user's display unit is kg, convert when you speak (1 kg = 2.2046 lb) and round to 2.5 kg.`;

export const MAX_HOPS = 6;
export const FINAL_TURN_NOTE = '[app] Tool budget for this reply is used up. Answer now from what you already have, without calling tools; say briefly if something could not be checked.';

// Runs one assistant turn (which may include several tool round-trips).
//   history: Anthropic MessageParam[] (user/assistant turns so far, incl. tool blocks)
//   onText(fullTextSoFar), onProposal({ id, name, input }), onStatus(string)
// Returns the updated history (append-only — the model's content is kept verbatim).
// Tool round-trips are capped at MAX_HOPS; the last allowed request is sent with
// tool_choice 'none' plus a note, so the reply always ends in a real answer.
//   onUsage({ model, usage, cost }) after every request (cost in $ at list prices)
export async function runCoachTurn({ history, state, program, catalog, onText, onProposal, onStatus, onUsage, signal }) {
  const mode = aiMode();
  const model = modelFor('coach', { mode });
  try {
    const c = await client(mode);
    const system = [
      { type: 'text', text: COACH_SYSTEM, cache_control: { type: 'ephemeral' } },
      { type: 'text', text: 'Training snapshot:\n' + coachContext(state, program), cache_control: { type: 'ephemeral' } },
    ];
    const tools = COACH_TOOLS;
    let messages = [...history];
    for (let hop = 0; hop < MAX_HOPS; hop++) {
      const final = hop === MAX_HOPS - 1;
      let text = '';
      const stream = c.beta.messages.stream({
        model,
        max_tokens: mode === 'relay' ? 8000 : 16000,
        system,
        tools,
        ...(final ? { tool_choice: { type: 'none' } } : {}),
        messages: withCacheBreakpoints(messages),
        ...requestParams(model, mode),
      }, { signal });
      for await (const ev of stream) {
        if (ev.type === 'content_block_delta' && ev.delta.type === 'text_delta') {
          text += ev.delta.text;
          onText && onText(text);
        } else if (ev.type === 'content_block_start' && ev.content_block.type === 'tool_use') {
          onStatus && onStatus(toolStatus(ev.content_block.name));
        }
      }
      const msg = await stream.finalMessage();
      const billed = msg.model && PRICES[msg.model] ? msg.model : model;
      recordUsage(billed, msg.usage, mode);
      if (onUsage && msg.usage) onUsage({ model: billed, usage: msg.usage, cost: costOf(billed, usageRow(msg.usage)) });
      messages = [...messages, { role: 'assistant', content: msg.content }];
      if (msg.stop_reason === 'refusal') {
        throw new AIError('refusal', "The coach can't help with that request.");
      }
      const toolUses = msg.content.filter(b => b.type === 'tool_use');
      if (msg.stop_reason === 'max_tokens') {
        // A cut-off tool call can't be run safely. A cut-off answer is kept as-is
        // (history stays append-only, which preserved thinking requires) and the
        // user can ask to continue.
        if (toolUses.length) throw new AIError('truncated', 'The response was cut off — try asking for less at once.');
        onStatus && onStatus('');
        break;
      }
      if (msg.stop_reason !== 'tool_use' || !toolUses.length) break;
      const results = toolUses.map(tu => {
        if (final) return { type: 'tool_result', tool_use_id: tu.id, is_error: true, content: 'Not run: the tool budget for this reply was used up.' };
        if (WRITE_TOOLS.has(tu.name)) {
          // Validate against the real program / progression engine before showing
          // anything: an invalid proposal goes back to the model to fix.
          const v = validateProposal(tu.name, tu.input || {}, { state, program, catalog });
          if (!v.ok) {
            onProposal && onProposal({ id: tu.id, name: tu.name, input: tu.input, rejected: true });
            return { type: 'tool_result', tool_use_id: tu.id, is_error: true, content: `Not shown to the user — it can't be applied as written:\n- ${v.errors.join('\n- ')}\nFix these (check get_program / search_exercises) and call ${tu.name} again with the corrected proposal.` };
          }
          onProposal && onProposal({ id: tu.id, name: tu.name, input: tu.input });
          return { type: 'tool_result', tool_use_id: tu.id, content: 'Shown to the user as an approval card. They will apply or dismiss it; do not assume it was applied.' };
        }
        let out;
        try {
          if (CARD_READ_TOOLS.has(tu.name)) {
            // Runs now (the model gets the plan) and renders a card the user can start.
            const plan = buildWorkoutPlan(state, tu.input || {});
            onProposal && onProposal({ id: tu.id, name: tu.name, input: tu.input, plan });
            out = planForModel(plan);
          } else {
            out = runReadTool(tu.name, tu.input || {}, { state, program, catalog });
          }
        } catch (e) { return { type: 'tool_result', tool_use_id: tu.id, is_error: true, content: String(e?.message || e) }; }
        return { type: 'tool_result', tool_use_id: tu.id, content: JSON.stringify(out) };
      });
      if (final) { messages = [...messages, { role: 'user', content: results }]; break; }
      // Next request is the last one allowed: tell the model, and send it text-only.
      const nextIsFinal = hop + 1 === MAX_HOPS - 1;
      messages = [...messages, { role: 'user', content: nextIsFinal ? [...results, { type: 'text', text: FINAL_TURN_NOTE }] : results }];
      onStatus && onStatus(nextIsFinal ? 'Wrapping up…' : 'Thinking…');
    }
    if (mode === 'relay') refreshRelayStatus({ force: true });
    return messages;
  } catch (err) {
    if (mode === 'relay') refreshRelayStatus({ force: true });
    throw await friendly(err, mode);
  }
}

function toolStatus(name) {
  return {
    get_program: 'Reading your program…',
    get_exercise_history: 'Checking your lift history…',
    get_recent_workouts: 'Reviewing recent workouts…',
    get_weekly_volume: 'Counting weekly sets…',
    get_exercise_guide: 'Looking up the technique…',
    search_exercises: 'Searching exercises…',
    propose_program_changes: 'Drafting changes…',
    propose_new_program: 'Building your program…',
    build_workout: 'Building today’s workout…',
  }[name] || 'Working…';
}

// ——— one-shot completions (quick log, debrief, weekly review) ——————————————

/** Plain-text completion. task picks the model (default: the fast model). */
export async function completeText({ system, prompt, maxTokens = 2000, signal, task = 'debrief' }) {
  const mode = aiMode();
  const model = modelFor(task, { mode });
  try {
    const c = await client(mode);
    const stream = c.beta.messages.stream({
      model, max_tokens: model === 'claude-haiku-4-5' ? maxTokens : maxTokens + THINK_HEADROOM,
      system: [{ type: 'text', text: system, cache_control: { type: 'ephemeral' } }],
      messages: [{ role: 'user', content: prompt }],
      ...requestParams(model, mode, { effort: FAST_EFFORT }),
    }, { signal });
    const msg = await stream.finalMessage();
    recordUsage(model, msg.usage, mode);
    if (mode === 'relay') refreshRelayStatus({ force: true });
    if (msg.stop_reason === 'refusal') throw new AIError('refusal', 'Declined.');
    return msg.content.filter(b => b.type === 'text').map(b => b.text).join('').trim();
  } catch (err) {
    throw await friendly(err, mode);
  }
}

// Structured output: returns a JSON object matching `schema` (JSON Schema with
// additionalProperties:false + required), or throws AIError.
export async function completeJSON({ system, prompt, schema, maxTokens = 2000, signal, task = 'quicklog' }) {
  const mode = aiMode();
  const model = modelFor(task, { mode });
  try {
    const c = await client(mode);
    const extra = requestParams(model, mode, { effort: FAST_EFFORT });
    const stream = c.beta.messages.stream({
      model, max_tokens: model === 'claude-haiku-4-5' ? maxTokens : maxTokens + THINK_HEADROOM,
      system: [{ type: 'text', text: system, cache_control: { type: 'ephemeral' } }],
      messages: [{ role: 'user', content: prompt }],
      ...extra,
      output_config: { ...(extra.output_config || {}), format: { type: 'json_schema', schema } },
    }, { signal });
    const msg = await stream.finalMessage();
    recordUsage(model, msg.usage, mode);
    if (mode === 'relay') refreshRelayStatus({ force: true });
    if (msg.stop_reason === 'refusal') throw new AIError('refusal', 'Declined.');
    if (msg.stop_reason === 'max_tokens') throw new AIError('truncated', 'The AI reply was cut off. Try again.');
    const text = msg.content.filter(b => b.type === 'text').map(b => b.text).join('');
    try { return JSON.parse(text); } catch { throw new AIError('parse', 'The AI reply could not be read.'); }
  } catch (err) {
    throw await friendly(err, mode);
  }
}

/**
 * Weekly review with applicable suggestions (program patches, a deload, volume
 * rebalancing). Every program patch is dry-run against the real program and
 * dropped if nothing in it would apply. → { headline, summary, wins, focus, suggestions[] }
 */
export async function runWeeklyReview({ state, program, catalog = null, signal }) {
  const { system, prompt } = weeklyReviewPrompt(state, program);
  const raw = await completeJSON({ system, prompt, schema: WEEKLY_REVIEW_SCHEMA, maxTokens: 3000, signal, task: 'review' });
  return normalizeReview(raw, { state, program, catalog });
}

/** Validates a key with a minimal request (own key only). */
export async function testKey() {
  const c = await client('byo');
  try {
    await c.models.retrieve(getAIConfig().model);
    return true;
  } catch (err) {
    throw await friendly(err, 'byo');
  }
}

// ——— "Ask about this" ————————————————————————————————————————————————————
/**
 * Open the coach pre-seeded with something the user is looking at.
 *   openCoachWith({ kind: 'exercise', exerciseId }, setRoute)
 *   openCoachWith({ kind: 'pr', exerciseId, w, reps, date }, setRoute)
 *   openCoachWith({ kind: 'stall', exerciseId }, setRoute)
 *   openCoachWith({ kind: 'session', sessionId }, setRoute)
 *   openCoachWith({ kind: 'muscle', muscle }, setRoute)
 *   openCoachWith({ kind: 'program', programId }, setRoute)
 *   openCoachWith({ kind: 'free', prompt }, setRoute)
 * The coach shows what it's about, offers questions for it, and attaches the
 * context to the first message so the model looks the data up with its tools.
 */
export function openCoachWith(context, setRoute) {
  const ctx = {};
  for (const k of ['kind', 'exerciseId', 'sessionId', 'programId', 'muscle', 'w', 'reps', 'date', 'prompt', 'label']) {
    if (context && context[k] != null && context[k] !== '') ctx[k] = typeof context[k] === 'string' ? context[k].slice(0, 300) : context[k];
  }
  if (!ctx.kind) ctx.kind = ctx.exerciseId ? 'exercise' : ctx.sessionId ? 'session' : 'free';
  if (typeof setRoute === 'function') setRoute('ai-coach', { ctx });
  return ctx;
}
export { contextBlockFor };
